import { useState, useEffect, useCallback, useRef } from 'react';
import { useFetchPatient, useFetchMeasures } from '../state/data/patient';
import { useFetchPatientFile } from '../state/data/patientFile';
import { useFetchRecentClosedTasksForPatient } from '../state/data/task';

// The patient-file view caps tasks at 100 ORDER BY due_at ASC → a patient with 100+ done
// loses recent completions. Merge a recency-sorted recent-closed fetch so the "Done"
// preview stays correct at scale (view rows win on dedupe — they carry nested `.template`).
const RECENT_CLOSED_LIMIT = 40;
function mergeTasks(viewTasks, closedTasks) {
  const out = [];
  const seen = new Set();
  // Dedupe by task_id, falling back to dedupe_key;
  // a row with neither is kept rather than collapsed onto a shared empty key.
  const push = (t) => {
    const k = t.task_id != null ? `id:${t.task_id}` : (t.dedupe_key != null ? `dk:${t.dedupe_key}` : null);
    if (k) {
      if (seen.has(k)) return;
      seen.add(k);
    }
    out.push(t);
  };
  (viewTasks || []).forEach(push); // view first → wins on dedupe
  (closedTasks || []).forEach(push); // recent-closed adds completions the view omitted
  return out;
}

// Patient-file data lifecycle for PatientFileShell: one patient-file view fetch + two
// external Withings reads (measures + live profile) + the post-mutation poll loop.
// Tabs render from this — they never fetch independently (keeps switches instant).
export default function usePatientFileData({ patient, onRefreshTasks }) {
  const fetchPatient = useFetchPatient();
  const fetchMeasures = useFetchMeasures();
  const fetchPatientFile = useFetchPatientFile();
  const fetchRecentClosed = useFetchRecentClosedTasksForPatient();

  const [dataLoading, setDataLoading] = useState(true);
  const [measures, setMeasures] = useState(null);
  const [carePlan, setCarePlan] = useState(null);
  const [carePeriod, setCarePeriod] = useState(null);
  const [measureStates, setMeasureStates] = useState([]);
  const [patientDetails, setPatientDetails] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [consent, setConsent] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [intakesTotalCount, setIntakesTotalCount] = useState(0);
  const [bundledTasks, setBundledTasks] = useState([]);

  // Re-fetch the bundle once the WF17 cascade lands (a completion/reopen is
  // async — an immediate refetch would read the pre-cascade state).
  const taskRefreshTimer = useRef(null);
  const aliveRef = useRef(false);
  // Set inside the effect, not by useRef(true): Strict Mode's rehearsal unmount must re-arm it.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (taskRefreshTimer.current) clearTimeout(taskRefreshTimer.current);
    };
  }, []);

  const uid = patient.userid;

  useEffect(() => {
    let cancelled = false;
    if (taskRefreshTimer.current) clearTimeout(taskRefreshTimer.current);
    setDataLoading(true);
    setCarePlan(null);
    setCarePeriod(null);
    setMeasureStates([]);
    setMeasures(null);
    setInsurance(null);
    setConsent(null);
    setEligibility(null);
    setIntakes([]);
    setIntakesTotalCount(0);
    setBundledTasks([]);

    Promise.all([
      fetchPatientFile(uid).catch(() => null),
      fetchMeasures(uid).catch(() => null),
      fetchPatient(uid).catch(() => null),
      fetchRecentClosed(uid, { assignedToType: 'staff', limit: RECENT_CLOSED_LIMIT }).catch(() => null),
    ]).then(([bundle, measuresRes, detailsRes, recentClosed]) => {
      if (cancelled) return;
      setCarePeriod(bundle?.carePeriod || null);
      setCarePlan(bundle?.carePlan || null);
      setMeasureStates(bundle?.measureStates || []);
      setInsurance(bundle?.insurance || null);
      setConsent(bundle?.consent || null);
      setEligibility(bundle?.eligibility || null);
      setIntakes(Array.isArray(bundle?.intakes) ? bundle.intakes : []);
      setIntakesTotalCount(
        Number.isFinite(bundle?.intakesTotalCount)
          ? bundle.intakesTotalCount
          : (Array.isArray(bundle?.intakes) ? bundle.intakes.length : 0),
      );
      setBundledTasks(mergeTasks(bundle?.tasks || [], recentClosed?.tasks || []));
      setMeasures(measuresRes);
      setPatientDetails(detailsRes);
      setDataLoading(false);
    });

    return () => { cancelled = true; };
  }, [uid, fetchPatient, fetchMeasures, fetchPatientFile, fetchRecentClosed]);

  // Re-pull the bundle-derived state (care period, plan, eligibility, consent,
  // insurance, measure states, intakes, tasks) after an out-of-band mutation that
  // the task poll doesn't cover — e.g. a CMS eligibility check run from the
  // onboarding review flips care_period.status server-side. Leaves the Withings
  // reads (measures / live profile) untouched.
  const refreshBundle = useCallback(async () => {
    const [bundle, recentClosed] = await Promise.all([
      fetchPatientFile(uid).catch(() => null),
      fetchRecentClosed(uid, { assignedToType: 'staff', limit: RECENT_CLOSED_LIMIT }).catch(() => null),
    ]);
    if (!bundle) return null;
    setCarePeriod(bundle.carePeriod || null);
    setCarePlan(bundle.carePlan || null);
    setMeasureStates(bundle.measureStates || []);
    setInsurance(bundle.insurance || null);
    setConsent(bundle.consent || null);
    setEligibility(bundle.eligibility || null);
    setIntakes(Array.isArray(bundle.intakes) ? bundle.intakes : []);
    setIntakesTotalCount(
      Number.isFinite(bundle.intakesTotalCount)
        ? bundle.intakesTotalCount
        : (Array.isArray(bundle.intakes) ? bundle.intakes.length : 0),
    );
    setBundledTasks(mergeTasks(bundle.tasks || [], recentClosed?.tasks || []));
    // The caller that waits on a server-side status change reads the period it just stored.
    return bundle.carePeriod || null;
  }, [fetchPatientFile, fetchRecentClosed, uid]);

  // A complete/reopen is applied async by the WF17 cascade — poll the bundle
  // every 5s until the target task reaches the expected state (30s ceiling).
  // onGiveUp: called once when the ceiling passes and the task never reached the expected state.
  const pollTasksUntil = useCallback((taskId, isSatisfied, onGiveUp) => {
    // A caller may still be awaiting a slow write when the file closes: never start a chain after unmount.
    if (!aliveRef.current) return;
    if (taskRefreshTimer.current) clearTimeout(taskRefreshTimer.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 6; // 6 × 5s = 30s ceiling, then stop
    const tick = async () => {
      attempts += 1;
      let satisfied = false;
      try {
        const [bundle, recentClosed] = await Promise.all([
          fetchPatientFile(uid),
          fetchRecentClosed(uid, { assignedToType: 'staff', limit: RECENT_CLOSED_LIMIT }).catch(() => null),
        ]);
        const merged = mergeTasks(bundle?.tasks || [], recentClosed?.tasks || []);
        const t = merged.find((x) => String(x.task_id) === String(taskId));
        // Second arg = the full merged list, for predicates that also wait on a
        // cascade-created sibling (e.g. labs-order completion → WF17 creates the review task).
        satisfied = !!(t && isSatisfied(t, merged));
        // Adopt server truth ONLY once the refetch reflects the transition. The
        // WF17 cascade is async, so an early read still shows the old status —
        // writing it back would clobber the optimistic update out of Done.
        if (satisfied) setBundledTasks(merged);
      } catch { /* transient — retry next tick */ }
      if (!aliveRef.current) return;
      onRefreshTasks?.();
      if (satisfied || attempts >= MAX_ATTEMPTS) {
        taskRefreshTimer.current = null;
        if (!satisfied) onGiveUp?.();
        return;
      }
      taskRefreshTimer.current = setTimeout(tick, 5000);
    };
    taskRefreshTimer.current = setTimeout(tick, 5000);
  }, [fetchPatientFile, fetchRecentClosed, uid, onRefreshTasks, setBundledTasks]);


  // Merge prop data with fetched measures — prefer non-null values from either source.
  const mergedPatient = measures ? Object.fromEntries(
    Object.entries({ ...patient, ...measures }).map(([k, v]) => [k, v ?? patient[k]]),
  ) : patient;

  return {
    dataLoading,
    patient: mergedPatient,
    carePlan, setCarePlan,
    carePeriod, setCarePeriod,
    measureStates,
    patientDetails,
    insurance,
    consent,
    eligibility,
    intakes,
    intakesTotalCount,
    bundledTasks, setBundledTasks,
    pollTasksUntil,
    refreshBundle,
  };
}
